import {Injectable} from "@angular/core";
import {ITable} from "@feature/common";
import {BehaviorSubject, Observable, of, tap} from "rxjs";
import {InvoiceStatusWidgetFacade} from "@feature/invoice-status/services/invoice-status-widget.facade";

/**
 * This is a data service storing a table of line item data per each 'document'
 * level row, keyed on payables_register.uri. (The 'document' is a creditor invoice).
 *
 * This data service is a simple cache for http retrieved data, instead of using
 * ngrx store.
 *
 * If the data is not in the cache, query it via http and store it, if it is
 * already stored in the cache then return the cache entry instead of
 * re-querying.
 *
 * Multiple instances of this data service can/will exist, one per-widget instance
 * i.e. it is deliberately not provided via the 'root' injector.
 *
 * It is initialized with the widget, because the widget instance id, and whether
 * it is for an external product or mojo dashboards (which affects the labelling
 * of the invoice status) are http query parameters.
 */
@Injectable()
export class InvoiceStatusWidgetLineItemDataService {

  widgetInstanceId: number;
  external: boolean;
  cache: { [key: number]: ITable } = {};
  isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  constructor(private readonly invoiceWidgetFacade: InvoiceStatusWidgetFacade) { }

  public init(widgetInstanceId: number, external: boolean) {
    this.widgetInstanceId = widgetInstanceId;
    this.external = external;
  }

  public getLineItemData(payablesRegisterUri: number): Observable<ITable> {
    if (this.cache[payablesRegisterUri] === undefined) {
      this.isLoadingSubject.next(true);
      return this.invoiceWidgetFacade.getItemLevelData(this.widgetInstanceId, payablesRegisterUri, this.external).pipe(
        tap((itemLevelData: ITable) => {
          this.cache[payablesRegisterUri] = itemLevelData;
          this.isLoadingSubject.next(false);
        })
      );
    } else {
      return of(this.cache[payablesRegisterUri]);
    }
  }

}
